import { Box, Button, SxProps, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    InternalLinkSectionElement,
    InternalLinkSectionEntry,
} from '../../../graphql/pimcore/modules/siteContent/types/SiteContentElement';
import { buildRoute } from '../../../utils/openResourceLibraryItem';

interface InternalLinkSectionProps {
    section: InternalLinkSectionElement;
    backgroundColor?: string;
    contentPadding?: SxProps;
}

export const buttonStyleProps: SxProps = {
    padding: '4px 11px',
    gap: '10px',
    height: '2.75rem',

    backgroundColor: '#b75332',
    borderRadius: '5px',
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '1rem',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    color: 'white',
    '&:hover': {
        backgroundColor: '#e5673e',
    },
    '&:focus': {
        backgroundColor: '#e5673e',
    },
};

const getEntryLabel = (entry: InternalLinkSectionEntry): string => {
    if (entry.kind === 'resourceLibraryItemLinkFieldCollection') {
        return entry.linkText || entry.resourceLibraryItem.name;
    }
    return entry.linkText;
};

const getEntryRoute = (entry: InternalLinkSectionEntry): string => {
    if (entry.kind === 'resourceLibraryItemLinkFieldCollection') {
        return buildRoute(entry.resourceLibraryItem.id);
    }
    return entry.linkRoute;
};

export const InternalLinkSection: FunctionComponent<InternalLinkSectionProps> = ({
    section,
    backgroundColor,
    contentPadding,
}) => {
    const baseContentSx: SxProps = {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        alignItems: 'flex-start',
    };

    return (
        <Box sx={{ width: '100%', backgroundColor: backgroundColor ?? '#F2F2F2' }}>
            <Box
                sx={{
                    ...baseContentSx,
                    width: '100%',
                    margin: 'auto',
                    paddingTop: '1.5rem',
                    paddingBottom: '1.5rem',
                    ...(contentPadding ?? {}),
                }}
            >
                <Typography
                    component="h2"
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: '24px',
                        fontWeight: 700,
                    }}
                >
                    {section.title}
                </Typography>
                {section.text ? (
                    <Typography
                        component="p"
                        sx={{
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: '24px',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        {section.text}
                    </Typography>
                ) : null}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        width: '100%',
                    }}
                >
                    {section.links.map((entry) => {
                        const label = getEntryLabel(entry);
                        const route = getEntryRoute(entry);

                        return (
                            <Button
                                key={`${entry.kind}-${label}-${route}`}
                                variant="contained"
                                component={RouterLink}
                                to={route}
                                sx={{
                                    ...buttonStyleProps,
                                    width: 'fit-content',
                                }}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default InternalLinkSection;
